import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { secureDelete, secureGetPdf, secureGet, securePost } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import DeletePopup from "../../components/DeletePopup";
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";

import trashIcon from "../../icons/trashW.svg";
import exportIcon from "../../icons/export.svg";

const InvoiceDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();

  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [invoice, setInvoice] = useState({});
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/billing-invoices/${id}`, showSnackbar, { signal });
      const fetchedInvoice = response.data;
      setInvoice(fetchedInvoice);
      console.log("fetchedInvoice", fetchedInvoice);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch order:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [id, signal]);

  useEffect(() => {
    fetchInvoice();

    return () => {
      abortController.abort();
    };
  }, [id, abortController, fetchInvoice]);

  const quotesExist = invoice.deliveryNotes?.[0]?.carts?.[0]?.quotes && invoice.deliveryNotes?.[0]?.carts?.[0]?.quotes.length > 0;

  const renderStatut = () => (
    <div className="flex items-center ">
      <span
        className={`px-[5px] py-1 rounded-[10px] font-semibold text-[12px] ${quotesExist ? "bg-[#0AD69F] text-white cursor-pointer" : "bg-[#EF476F] text-white cursor-not-allowed"
          }`}
        onClick={() => quotesExist && handleStatutClick("DV")}
      >
        DV
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>


      <span
        className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white cursor-pointer"
        onClick={() => handleStatutClick("BC")}
      >
        BC
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>

      <span
        className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white cursor-pointer"
        onClick={() => handleStatutClick("BL")}
      >
        BL
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>

      <span
        className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white cursor-pointer"
      >
        FA
      </span>
    </div>
  );

  const handleStatutClick = (key) => {
    switch (key) {
      case "DV":
        handleConsultDV();
        break;
      case "BC":
        handleConsultBC();
        break;
      case "BL":
        handleConsultBL();
        break;
      default:
        break;
    }
  };

  const handleConsultDV = () => {
    history.push({
      pathname: `/quotes/details/${invoice.deliveryNotes?.[0]?.carts?.[0]?.quotes.id}`,
    });
  };

  const handleConsultBC = () => {
    history.push({
      pathname: `/orders/details/${invoice.deliveryNotes?.[0]?.carts?.[0]?.id}`,
    });
  };

  const handleConsultBL = () => {
    history.push({
      pathname: `/delivery-notes/details/${invoice.deliveryNotes?.[0]?.id}`,
    });
  };

  const handleDeleteClick = () => setDeletePopupVisible(true);

  const handleDeleteConfirm = async () => {
    try {
      await secureDelete(`/billing/invoices/delete/${invoice.id}`, showSnackbar);
      setDeletePopupVisible(false);
      history.push({
        pathname: "/invoices",
        state: {
          showSnackbar: true,
          snackbarMessage: t(`confirmation.invoiceDelete`),
          snackbarSeverity: 'success',
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 30,
        },
      });
    } catch (error) {
      setDeletePopupVisible(false);
      console.error("Error deleting invoice:", error);
    }
  };

  const handleIssueClick = async () => {
    if (!invoice?.id) return;
    setIssuing(true);
    try {
      await securePost(`/billing-invoices/${invoice.id}/issue`, {}, showSnackbar);
      showSnackbar(t('confirmation.invoiceIssued') || 'Invoice issued', 'success');
      fetchInvoice();
    } catch (error) {
      console.error("Failed to issue invoice:", error);
    } finally {
      setIssuing(false);
    }
  };

  const handleDownloadClick = async () => {
    try {
      const response = await secureGetPdf(`/billing-invoices/${invoice.id}/pdf`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoice.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download invoice:", error);
    }
  };

  const renderActionButtons = () => (
    <>
      {invoice?.status?.toLowerCase() === 'draft' && (
        <button
          className="flex items-center justify-center bg-[#043F88] text-white px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011] disabled:opacity-50"
          onClick={handleIssueClick}
          disabled={issuing}
        >
          {issuing ? t('actions.issuing') || 'Issuing...' : t('actions.issueInvoice') || 'Issue'}
        </button>
      )}
      <button
        className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
        onClick={handleDownloadClick}
      >
        <img src={exportIcon} alt="download" className="w-5 h-5" />
        {t('actions.downloadInvoice')}
      </button>
      <button
        className="flex items-center justify-center bg-[#EF476F] text-white px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
        onClick={handleDeleteClick}
      >
        <img src={trashIcon} alt="delete" className="w-5 h-5" />
        {t('actions.delete')}
      </button>
    </>
  );

  if (loading) return <LoadingLogo />;


  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.invoice_details")}</title>
      </Helmet>

      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-black">
          {t('titles.invoice_details')}
        </h1>

        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <p className="text-black font-semibold text-[24px]">
              {invoice.invoiceNumber}
            </p>
          </div>
          <div className="flex space-x-4">{renderActionButtons()}</div>
        </div>
      </div>

      <div className="w-full flex justify-between bg-white py-4 pl-[32px] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">
      <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
              {t('fields.invoice.clientId')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {invoice.grayClient?.companyName}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('columns.status')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {t(`billingInvoiceStatus.${invoice.status}`)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('columns.issuedAt')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(invoice.issuedAt)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('columns.dueDate')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(invoice.dueDate)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('fields.invoice.totalHT')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(invoice.priceHT)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('fields.invoice.totalTTC')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(invoice.priceTTC)}
          </p>
        </div>
      </div>

      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-3">
        {t('fields_types.list')}
      </h3>

      <div className="space-y-2">
        {invoice.details?.map((detail, index) => {
          const price = detail.price ?? detail.unitPrice ?? 0;
          const quantity = detail.quantity ?? 1;
          if (invoice.extraSeatsInvoice) {
            console.log("detail", detail);
            return (
              <div
                key={detail.id ?? index}
                className="grid grid-cols-[2fr_1fr_1fr_1fr] items-center bg-white px-4 py-2 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-4"
              >
                <div>
                  <p className="text-[#043F88] text-[16px] font-semibold">{detail.itemType ? (t(`fields.invoice.itemType_${detail.itemType}`)) : ''}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">{t('rows.quantity').toUpperCase()}</p>
                  <p className="text-[#043F88] text-[20px] font-medium">{quantity}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">{t('fields.invoice.unitPrice') || 'Prix unitaire'}</p>
                  <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(price)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">{t('rows.totalPrice')}</p>
                  <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(quantity * price * 1.2)}</p>
                </div>
              </div>
            );
          }
          const priceTotal = price * 1.2;
          return (
            <div
              key={detail.id ?? index}
              className="grid grid-cols-[2fr_1fr_1fr] items-center bg-white px-4 py-2 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-4"
            >
              <div>
                <p className="text-[#043F88] text-[16px] font-semibold">
                  {detail.itemType
                    ? `${t(`fields.invoice.itemType_${detail.itemType}`)}: ${detail.planName ? t(`plans.${detail.planName}`) : ''}${detail.planName && detail.includedSeats != null ? ' - ' : ''}${detail.includedSeats != null ? `${detail.includedSeats} ${t('fields.invoice.seats')}` : ''}`
                    : ''}
                </p>
              </div>
              <div className="text-center">
                <p className="text-[#707070] text-[12px] font-semibold">{t('fields.invoice.unitPrice') || 'Prix'}</p>
                <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(price)}</p>
              </div>
              <div className="text-center">
                <p className="text-[#707070] text-[12px] font-semibold">{t('rows.totalPrice')}</p>
                <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(priceTotal)}</p>
              </div>
            </div>
          );
        })}
      </div>

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default InvoiceDetails;
